import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import stockMovementService from '../services/stockMovementService';

const thStyle: React.CSSProperties = {
  padding: '10px 16px', fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.22em', textTransform: 'uppercase',
  color: '#6b6a5c', borderBottom: '1px solid rgba(92,78,51,0.15)', whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px', fontFamily: "'Inter', sans-serif", fontSize: 12,
  color: '#5b5a52', borderBottom: '1px solid rgba(241,245,249,1)', verticalAlign: 'middle',
};

export const ServiceWarehouseStockPage: React.FC = () => {
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');

  const { data: warehouses = [], isLoading: isLoadingWarehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => stockMovementService.getWarehouses(),
  });

  const { data: warehouseData, isLoading: isLoadingStock } = useQuery({
    queryKey: ['warehouse-stock', selectedWarehouseId],
    queryFn: () => stockMovementService.getWarehouseStock(selectedWarehouseId, 'INVENTORY'),
    enabled: !!selectedWarehouseId,
  });

  const stock = warehouseData?.stock || [];
  const totalBottles = stock.reduce((sum, item) => sum + item.quantityOnHand, 0);
  const totalML = totalBottles * 500;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Page header */}
      <div style={{ paddingBottom: 20, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 400, letterSpacing: '0.4em', textTransform: 'uppercase', color: '#8a8778', marginBottom: 8 }}>
          Inventory
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 36, fontWeight: 300, color: '#1c2033', lineHeight: 1 }}>
          Service Warehouse Stock
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 300, color: '#8a8778', marginTop: 8 }}>
          View oil scent inventory at service locations
        </div>
      </div>

      {/* Warehouse selector */}
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 20 }}>
        <label style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', display: 'block', marginBottom: 8 }}>
          Select Warehouse
        </label>
        <select
          value={selectedWarehouseId}
          onChange={(e) => setSelectedWarehouseId(e.target.value)}
          disabled={isLoadingWarehouses}
          style={{ width: '100%', maxWidth: 400, padding: '9px 12px', background: '#f4efe2', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52', outline: 'none' }}
        >
          <option value="">Select a warehouse...</option>
          {warehouses.map((wh) => (
            <option key={wh.id} value={wh.id}>{wh.name} ({wh.code})</option>
          ))}
        </select>
      </div>

      {/* Stock content */}
      {selectedWarehouseId && (
        <>
          {isLoadingStock ? (
            <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 48, textAlign: 'center' }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c' }}>Loading stock...</div>
            </div>
          ) : stock.length === 0 ? (
            <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 48, textAlign: 'center' }}>
              <svg width="40" height="40" fill="none" stroke="rgba(217,119,6,0.25)" viewBox="0 0 24 24" style={{ margin: '0 auto 12px' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, letterSpacing: '0.2em', color: '#6b6a5c' }}>NO STOCK AVAILABLE</div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#8a8778', marginTop: 6 }}>This warehouse has no oil products in stock.</div>
            </div>
          ) : (
            <>
              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                {[
                  { label: 'Total Products', value: stock.length, accent: 'rgba(90,140,201,0.35)' },
                  { label: 'Total Bottles', value: totalBottles.toFixed(2), accent: 'rgba(90,201,140,0.35)' },
                  { label: 'Total ML', value: totalML.toLocaleString(), accent: 'rgba(217,119,6,0.3)' },
                ].map(({ label, value, accent }) => (
                  <div key={label} style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 20, position: 'relative', overflow: 'hidden' }}>
                    <div style={{ height: 1, position: 'absolute', top: 0, left: 0, right: 0, background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 10 }}>{label}</div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 28, fontWeight: 300, background: 'linear-gradient(135deg, #d97706, #fbbf24)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Stock table */}
              <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'rgba(201,165,90,0.02)' }}>
                      <th style={thStyle}>Product</th>
                      <th style={thStyle}>SKU</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Bottles on Hand</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>ML Available</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Reserved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stock.map((item) => (
                      <tr
                        key={item.id}
                        style={{ transition: 'background 0.15s' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,250,252,1)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        <td style={tdStyle}>
                          <span style={{ color: '#1c2033', fontWeight: 500 }}>{item.product.name}</span>
                        </td>
                        <td style={tdStyle}>{item.product.sku}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: '#fbbf24' }}>
                            {item.quantityOnHand.toFixed(2)}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          {(item.quantityOnHand * 500).toLocaleString()} ML
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: '#6b6a5c' }}>
                          {item.quantityReserved.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
